/**
 * FounderAccessPopup — Exit-intent CTA for the Landing page.
 *
 * Triggers ONCE per visitor (14-day localStorage cookie) when their mouse
 * moves toward the browser-close zone. Pitches the founder-tier beta:
 *   "Pro free for 6 months — first 10 wholesalers only — in exchange for a
 *    30-min feedback call every 2 weeks."
 *
 * POSTs to `/api/founder-access/apply` which (a) inserts to Mongo,
 * (b) emails Tracy so he can call within 2 hours.
 *
 * Built mobile-friendly: on touch devices we fall back to a 60-second timer
 * since `mouseout`/`mouseleave` don't fire reliably.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { X, Crown, Flame, Phone, CheckCircle2, Loader2, AlertCircle, Gift } from "lucide-react";

const DISMISS_KEY = "founderAccess.dismissedUntil";
const APPLIED_KEY = "founderAccess.applied";
const VARIANT_KEY = "founderAccess.variant";
const DISMISS_DAYS = 14;
const TOUCH_DELAY_MS = 60_000; // 60s fallback for mobile

const API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

// Two-variant landing CTA copy.
// A = "Bundle is part of the Founder offer" (control).
// B = "Bundle yours either way" (risk-reducer — lifts conversion 15-30% per playbook).
const VARIANTS = {
  A: {
    bundleBanner:
      "Plus, instant bundle on signup: 25-page 60-Second Seller Call e-book + the Wholesale Assignment Contract PDF — emailed to you the second you apply.",
    submitLabel: "Apply for Priority Access →",
  },
  B: {
    bundleBanner:
      "Even if you don't get one of the 10 priority spots, you keep the bundle: 25-page 60-Second Seller Call e-book + Wholesale Assignment Contract PDF — emailed the moment you apply.",
    submitLabel: "Send my bundle + apply →",
  },
};

function pickVariant() {
  try {
    const stored = localStorage.getItem(VARIANT_KEY);
    if (stored === "A" || stored === "B") return stored;
    const v = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem(VARIANT_KEY, v);
    return v;
  } catch {
    return "A";
  }
}

function shouldShow() {
  try {
    if (localStorage.getItem(APPLIED_KEY) === "1") return false;
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return Date.now() > until;
  } catch {
    return true;
  }
}

function recordDismiss() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch { /* ignore */ }
}

function recordApplied() {
  try { localStorage.setItem(APPLIED_KEY, "1"); } catch { /* ignore */ }
}


export default function FounderAccessPopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [spots, setSpots] = useState(null);
  const [variant, setVariant] = useState("A");
  const [form, setForm] = useState({
    name: "", email: "", market: "", deals_closed_12mo: "", biggest_pain: "",
  });
  const armedRef = useRef(false);

  // Assign variant on mount so it's stable across re-renders.
  useEffect(() => { setVariant(pickVariant()); }, []);

  // Arm exit-intent listener (or touch-fallback) on mount
  useEffect(() => {
    if (!shouldShow()) return undefined;

    const fire = () => {
      if (armedRef.current) return;
      if (!shouldShow()) return;
      armedRef.current = true;
      setOpen(true);
    };

    // Desktop: mouseout to top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && (!e.relatedTarget && !e.toElement)) {
        fire();
      }
    };
    document.addEventListener("mouseout", onMouseOut);

    // Mobile / touch fallback — 60s after first interaction
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    let touchTimer;
    if (isTouch) {
      touchTimer = setTimeout(fire, TOUCH_DELAY_MS);
    }

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (touchTimer) clearTimeout(touchTimer);
    };
  }, []);

  // Programmatic open — fired by the live spots ticker on the hero, or any
  // other CTA that wants to surface this popup without waiting for exit-intent.
  // Bypasses `shouldShow()` so even dismissed/applied users can re-open it.
  useEffect(() => {
    const onForceOpen = () => setOpen(true);
    window.addEventListener("founder-access:open", onForceOpen);
    return () => window.removeEventListener("founder-access:open", onForceOpen);
  }, []);

  // Fetch remaining spots when opening
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/founder-access/spots`);
        if (!cancelled) setSpots(r.data);
      } catch {
        // non-blocking — just don't show the badge
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Escape key closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    if (!done) recordDismiss();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.market.trim()) {
      setError("Name, email, and market are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        market: form.market.trim(),
        deals_closed_12mo: form.deals_closed_12mo
          ? Math.max(0, parseInt(form.deals_closed_12mo, 10) || 0)
          : null,
        biggest_pain: form.biggest_pain.trim() || null,
        source: "landing_exit_popup",
        variant,
      };
      await axios.post(`${API_BASE}/api/founder-access/apply`, payload);
      recordApplied();
      setDone(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || "Couldn't submit — please email tracy@ispeedtoclose.com directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const spotsLeft = spots?.remaining ?? null;
  const spotsLow = spotsLeft !== null && spotsLeft <= 5;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-[fadeIn_180ms_ease]"
      data-testid="founder-access-popup"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient strip */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-emerald-50 to-amber-50 rounded-t-2xl border-b border-emerald-100">
          <button
            type="button"
            className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-white/60 text-[var(--ink-3)]"
            onClick={handleClose}
            data-testid="founder-popup-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white">
            <Crown size={11} /> Priority Access · invite-only
          </div>
          {spotsLeft !== null && (
            <div
              className={`mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${spotsLow ? "text-rose-700" : "text-emerald-700"}`}
              data-testid="founder-spots-badge"
            >
              <Flame size={10} />
              {spotsLeft > 0
                ? <>Only <strong>{spotsLeft} of {spots.max_spots}</strong> spots left</>
                : "Waitlist only — closed"}
            </div>
          )}
          <h2 className="font-display font-black text-2xl text-[var(--ink)] mt-2 leading-tight">
            Wait — get Pro <em className="italic text-emerald-700">free</em> for 3 months
          </h2>
          <p className="text-sm text-[var(--ink-2)] mt-2">
            We&apos;re hand-picking <strong>10 wholesalers</strong> as our Priority Cohort.
            Full Pro access — Apex 2.0 (AI answers every call), Cash Buyer Engine, Heatmap, the whole stack —
            <strong> free for 3 months</strong>. In exchange, one 30-min feedback call every 2 weeks.
          </p>
          <div
            className="mt-3 flex items-start gap-2 text-xs text-emerald-900 bg-emerald-100/70 border border-emerald-200 rounded-lg px-3 py-2"
            data-testid={`founder-bundle-banner-${variant}`}
          >
            <Gift size={14} className="shrink-0 mt-0.5 text-emerald-700" />
            <div>{VARIANTS[variant].bundleBanner}</div>
          </div>
        </div>

        {/* Body */}
        {done ? (
          <div className="p-8 text-center" data-testid="founder-popup-done">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mb-3">
              <CheckCircle2 size={28} className="text-emerald-600" />
            </div>
            <div className="font-display font-bold text-xl text-[var(--ink)]">
              You&apos;re in line.
            </div>
            <p className="text-sm text-[var(--ink-2)] mt-2 max-w-sm mx-auto">
              I&apos;ll personally call you within <strong>2 hours</strong> to walk you through
              the platform and get your priority access activated. Watch your phone.
            </p>
            <div className="mt-3 flex items-start gap-2 text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-w-sm mx-auto text-left">
              <Gift size={14} className="shrink-0 mt-0.5 text-emerald-700" />
              <div>
                Your <strong>60-Second Seller Call e-book</strong> and the <strong>Assignment Contract PDF</strong> are on the way to your inbox right now.
              </div>
            </div>
            <p className="text-xs text-[var(--ink-3)] mt-3 italic">
              — Tracy, iSpeedToClose
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="btn-money text-xs mt-5"
              data-testid="founder-popup-finish"
            >
              Got it
            </button>
          </div>
        ) : (
          <form className="p-6 space-y-4" onSubmit={handleSubmit} data-testid="founder-popup-form">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                Your name *
              </label>
              <input
                type="text"
                className="input mt-1 w-full"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Wholesaler"
                required
                data-testid="founder-popup-name"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                Best email *
              </label>
              <input
                type="email"
                className="input mt-1 w-full"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@yourdomain.com"
                required
                data-testid="founder-popup-email"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                  Primary market *
                </label>
                <input
                  type="text"
                  className="input mt-1 w-full"
                  value={form.market}
                  onChange={(e) => setForm({ ...form, market: e.target.value })}
                  placeholder="Dallas, TX"
                  required
                  data-testid="founder-popup-market"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                  Deals closed (12mo)
                </label>
                <input
                  type="number"
                  min="0"
                  className="input mt-1 w-full"
                  value={form.deals_closed_12mo}
                  onChange={(e) => setForm({ ...form, deals_closed_12mo: e.target.value })}
                  placeholder="e.g. 4"
                  data-testid="founder-popup-deals"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
                Biggest pain in your wholesaling business right now
              </label>
              <textarea
                className="input mt-1 w-full"
                rows={2}
                value={form.biggest_pain}
                onChange={(e) => setForm({ ...form, biggest_pain: e.target.value })}
                placeholder="e.g. Finding buyers for my deals fast enough…"
                data-testid="founder-popup-pain"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-[10px] text-[var(--ink-3)] flex items-center gap-1">
                <Phone size={10} /> I personally call every applicant within 2 hours.
              </div>
              <button
                type="submit"
                className="btn-money text-sm"
                disabled={submitting}
                data-testid="founder-popup-submit"
              >
                {submitting ? (
                  <><Loader2 size={14} className="animate-spin" /> Submitting…</>
                ) : (
                  <>{VARIANTS[variant].submitLabel}</>
                )}
              </button>
            </div>

            <p className="text-[10px] text-[var(--ink-3)] text-center pt-2 border-t border-[var(--line)]">
              No card. No commitment. Just feedback in exchange for free Pro.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
